/**
 * Google Sheets PaymentMethod Repository
 *
 * Google Sheets API를 사용하여 실제 스프레드시트에서 결제수단 데이터를 관리합니다.
 */

import type { PaymentMethod } from '../types/PaymentMethod'
import type { PaymentMethodRepository } from './PaymentMethodRepository'
import { GoogleSheetsClient } from '@/core/api/GoogleSheetsClient'
import { SHEET_NAMES } from '@/core/config/constants'
import { getWorkspaceClient } from '@/core/workspace/WorkspaceContext'

/**
 * Spreadsheet 행을 PaymentMethod 객체로 변환
 *
 * 첫 행은 헤더이므로 2행부터 데이터입니다.
 * 컬럼 순서: id, name, type, ownerId, memo, sortOrder, isActive
 */
function rowToPaymentMethod(row: string[]): PaymentMethod {
  return {
    id: row[0] || '',
    name: row[1] || '',
    type: (row[2] || 'cash') as PaymentMethod['type'],
    ownerId: row[3] || '',
    memo: row[4] || '',
    isActive: row[6] === 'true' || row[6] === 'TRUE',
  }
}

/**
 * PaymentMethod 객체를 Spreadsheet 행으로 변환
 *
 * 컬럼 순서: id, name, type, ownerId, memo, sortOrder, isActive
 */
function paymentMethodToRow(pm: PaymentMethod): string[] {
  return [
    pm.id,
    pm.name,
    pm.type,
    pm.ownerId,
    pm.memo,
    '0', // sortOrder - default 0 (PaymentMethod 타입에는 없지만 Google Sheets에 존재)
    pm.isActive.toString(),
  ]
}

/**
 * Google Sheets PaymentMethod Repository 구현
 */
export class GoogleSheetsPaymentMethodRepository
  implements PaymentMethodRepository
{
  constructor(_client?: GoogleSheetsClient) {
    // client 파라미터는 테스트 호환성을 위해 유지하지만 사용하지 않음
  }

  /**
   * 현재 워크스페이스의 GoogleSheetsClient 가져오기
   */
  private getClient(): GoogleSheetsClient {
    return getWorkspaceClient()
  }

  /**
   * 모든 결제수단 조회
   */
  async findAll(): Promise<PaymentMethod[]> {
    // 시트에서 모든 데이터 읽기
    const rows = await this.getClient().readSheet(SHEET_NAMES.PAYMENT_METHODS)

    // 첫 행은 헤더이므로 제외
    const dataRows = rows.slice(1)

    // 각 행을 PaymentMethod 객체로 변환
    return dataRows
      .filter((row) => row.length > 0) // 빈 행 제외
      .map(rowToPaymentMethod)
  }

  /**
   * 활성화된 결제수단만 조회
   */
  async findActive(): Promise<PaymentMethod[]> {
    const all = await this.findAll()
    return all.filter((pm) => pm.isActive)
  }

  /**
   * ID로 결제수단 조회
   */
  async findById(id: string): Promise<PaymentMethod | null> {
    const all = await this.findAll()
    return all.find((pm) => pm.id === id) || null
  }

  /**
   * 사용자별 결제수단 조회
   */
  async findByOwnerId(ownerId: string): Promise<PaymentMethod[]> {
    const all = await this.findAll()
    return all.filter((pm) => pm.ownerId === ownerId && pm.isActive)
  }

  /**
   * 새 결제수단 생성
   *
   * Google Sheets에 새로운 결제수단을 추가합니다.
   * ID는 자동으로 생성됩니다 (타임스탬프 기반).
   */
  async create(input: Omit<PaymentMethod, 'id'>): Promise<PaymentMethod> {
    // 1. 새 ID 생성 (타임스탬프 기반)
    const newId = `pm_${Date.now()}`

    // 2. 새 결제수단 객체 생성
    const newPaymentMethod: PaymentMethod = {
      id: newId,
      ...input,
    }

    // 3. Google Sheets에 추가
    const row = paymentMethodToRow(newPaymentMethod)
    await this.getClient().appendSheet(SHEET_NAMES.PAYMENT_METHODS, [row])

    return newPaymentMethod
  }

  /**
   * 결제수단 수정
   *
   * 특정 결제수단의 정보를 업데이트합니다.
   * 부분 업데이트를 지원합니다 (Partial).
   */
  async update(id: string, input: Partial<PaymentMethod>): Promise<PaymentMethod> {
    // 1. 전체 데이터 읽기
    const rows = await this.getClient().readSheet(SHEET_NAMES.PAYMENT_METHODS)
    const dataRows = rows.slice(1)

    // 2. 해당 행 찾기
    const rowIndex = dataRows.findIndex((row) => row[0] === id)
    if (rowIndex === -1) {
      throw new Error(`결제수단을 찾을 수 없습니다: ${id}`)
    }

    // 3. 기존 데이터와 병합
    const existingRow = dataRows[rowIndex]!
    const existingPaymentMethod = rowToPaymentMethod(existingRow)
    const updated: PaymentMethod = {
      ...existingPaymentMethod,
      ...input,
      id, // ID는 변경 불가
    }

    // 4. 시트 업데이트 (헤더 +1, 0-based index +1 = +2)
    const sheetRowIndex = rowIndex + 2
    const range = `A${sheetRowIndex}:G${sheetRowIndex}`
    await this.getClient().updateSheet(
      SHEET_NAMES.PAYMENT_METHODS,
      range,
      [paymentMethodToRow(updated)]
    )

    return updated
  }

  /**
   * 결제수단 삭제 (Soft Delete)
   *
   * 실제로 데이터를 삭제하는 대신 isActive를 false로 설정합니다.
   * 이는 데이터 무결성을 유지하고, 거래 기록의 결제수단 정보를 보존합니다.
   */
  async delete(id: string): Promise<void> {
    // Soft delete: isActive를 false로 설정
    await this.update(id, { isActive: false })
  }
}
